import React, { useEffect, useState } from "react";
import { ConnectButton } from "360dialog-connect-button";
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Button,
  Checkbox,
  Heading,
  Link,
  Text,
  VStack,
} from "@chakra-ui/react";
import useMerchantStore from "hooks/use-merchant-store";
import { Socket } from "socket.io-client";
import { SocketMessage } from "entities/ISocketArgs";
import { toast } from "react-toastify";
import { MdContentCopy } from "react-icons/md";
import { useWebSocket } from "hooks/use-socket";
import MessagePreviewSnippet from "./MessagePreviewSnippet";

type Pre360DialogRedirectProps = {
  setConsentGiven: (param: true) => void;
};

export const Pre360DialogRedirect = ({
  setConsentGiven,
}: Pre360DialogRedirectProps) => {
  const partnerId = process.env.REACT_APP_360_PARTNER_ID;

  const { merchant } = useMerchantStore();
  const [consent, setConsent] = useState({
    billing: false,
    otp: false,
    freshFacebookAccount: false,
  });

  return (
    <VStack w="full" alignItems="flex-start">
      <Heading fontSize="xl" alignSelf="center">
        Before we begin, be aware that:
      </Heading>
      <Checkbox
        isChecked={consent.freshFacebookAccount}
        onChange={(e) =>
          setConsent({ ...consent, freshFacebookAccount: e.target.checked })
        }
      >
        <Text fontSize="xs">
          We suggest using an existing Facebook account for WhatsApp sign up (as
          new profiles are sometimes delayed by Meta);
        </Text>
      </Checkbox>
      <Checkbox
        isChecked={consent.billing}
        onChange={(e) => setConsent({ ...consent, billing: e.target.checked })}
      >
        <Text fontSize="xs">
          As you register with 360Dialog, rest assured that all billing will go
          via Fuzey as per your existing agreement; and
        </Text>
      </Checkbox>
      <Checkbox
        isChecked={consent.otp}
        onChange={(e) => setConsent({ ...consent, otp: e.target.checked })}
      >
        <Text fontSize="xs">
          When registering for WhatsApp activation, please use your new Fuzey
          number and opt for verification via text as the code will be shown
          here.
        </Text>
      </Checkbox>
      <Button
        as={ConnectButton}
        partnerId={partnerId!}
        onAfterClick={() => {
          setConsentGiven(true);
        }}
        colorScheme="green"
        label="Connect"
        queryParameters={{
          partner: merchant.public_id,
          name: merchant.name,
          redirect_url: `${window.location.origin}/public/callback/success?integration=whatsapp`,
        }}
        alignSelf="center"
        disabled={
          !consent.billing || !consent.freshFacebookAccount || !consent.otp
        }
      />
    </VStack>
  );
};

export const Post360DialogRedirect = () => {
  const { socket } = useWebSocket();
  const [newMessage, setNewMessage] = useState<string | React.ReactNode>("");
  const { merchant } = useMerchantStore();

  const regex = new RegExp("(\\d\\d\\d\\d\\d\\d)|(\\d\\d\\d-\\d\\d\\d)");

  const copyToClipboard = (otp: string) => {
    navigator.clipboard.writeText(otp);
  };

  const handleNewMessageEvent = (args: SocketMessage) => {
    if (args.message.is_incoming && regex.test(args.message.body)) {
      const message = args.message.body;
      const regExpExec = regex.exec(message)!;
      const otp = regExpExec[0];

      setNewMessage(
        <Text>
          {message.substring(0, regExpExec.index)}
          <Text
            as={Link}
            onClick={() => copyToClipboard(otp.replace("-", ""))}
            position="relative"
          >
            {otp}
            <Text
              as={MdContentCopy}
              display="inline-block"
              variant="unstyled"
              ml={0.5}
              __css={{ svg: { height: "0.8rem", width: "0.8rem" } }}
            />
          </Text>
          {message.substring(regExpExec.index + otp.length, message.length)}
        </Text>
      );
    }
  };

  const inboundMessageListener = (args: SocketMessage) => {
    if (args.merchantId && args.message) {
      handleNewMessageEvent(args);
    } else {
      /* eslint-disable no-console */
      console.error("socket error:", args);
      /* eslint-enable no-console */
      toast.error(
        "Couldn't receive a message. Feel free to skip this step for now then."
      );
    }
  };

  useEffect(() => {
    socket.on("inbound_message", inboundMessageListener);
    return () => {
      socket.off("inbound_message", inboundMessageListener);
    };
  }, [socket]);

  return !newMessage ? (
    <Alert>
      <AlertIcon />
      <AlertTitle>
        Please use your new number{" "}
        {merchant.channels.find((c) => c.channelName === "sms")?.identifier} for
        WhatsApp sign up and opt for verification via text. We&apos;ll show you
        the code here when it arrives!
      </AlertTitle>
    </Alert>
  ) : (
    <MessagePreviewSnippet newMessage={newMessage} />
  );
};
